<template>
  <div class="profile-page" :class="{ 'profile-page--mobile': !isPC }">
    <form class="profile-form" @submit.prevent="handleSave">
      <section class="form-section avatar-section">
        <label class="field-label">头像</label>

        <div class="avatar-row">
          <button
            type="button"
            class="avatar-trigger"
            :disabled="uploading.avatar"
            aria-label="上传头像"
            @click="triggerUpload"
          >
            <img
              v-if="userStore.userData.userImg"
              :src="userStore.userData.userImg"
              alt="用户头像"
              class="avatar-image"
            />
            <div v-else class="avatar-placeholder">
              <SvgIcon name="settings-profile" :size="28" color="#cbd5e1" />
            </div>
            <span class="avatar-overlay" aria-hidden="true">
              <SvgIcon name="settings-avatar-upload" :size="24" color="#ffffff" />
            </span>
          </button>

          <button
            type="button"
            class="replace-btn"
            :disabled="uploading.avatar"
            @click="triggerUpload"
          >
            {{ uploading.avatar ? '上传中…' : '替换图片' }}
          </button>
        </div>

        <p class="field-hint">建议格式：JPG，PNG（150*150px）</p>
      </section>

      <section class="form-section">
        <label class="field-label" for="profile-name"> 昵称<span class="required">*</span> </label>
        <el-input
          id="profile-name"
          v-model="profile.uName"
          class="profile-input"
          placeholder="请输入昵称"
          maxlength="50"
        />
      </section>

      <section class="form-section">
        <label class="field-label" for="profile-phone">联系方式</label>
        <el-input
          id="profile-phone"
          v-model="profile.phone"
          class="profile-input"
          placeholder="可填写微信 / QQ / 电话 / 邮箱等"
          maxlength="20"
        />
      </section>

      <section class="form-section">
        <label class="field-label" for="profile-company">公司</label>
        <el-input
          id="profile-company"
          v-model="profile.company"
          class="profile-input"
          placeholder="请输入公司名称"
          maxlength="100"
        />
      </section>

      <section class="form-section">
        <label class="field-label" for="profile-website">网站链接</label>
        <el-input
          id="profile-website"
          v-model="profile.website"
          class="profile-input"
          placeholder="请检查您的链接是否能打开"
          maxlength="200"
        />
      </section>

      <section class="form-section">
        <label class="field-label" for="profile-about">简介</label>
        <el-input
          id="profile-about"
          v-model="profile.about"
          class="profile-input"
          placeholder="请输入个人简介"
          maxlength="500"
        />
      </section>

      <section class="form-section">
        <label class="field-label" for="profile-address">地址</label>
        <el-input
          id="profile-address"
          v-model="profile.address"
          class="profile-input"
          placeholder="请输入地址"
          maxlength="200"
        />
      </section>

      <button type="submit" class="save-btn" :disabled="saving || !profile.uName.trim()">
        {{ saving ? '保存中…' : '保存' }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
  import { reactive, ref, watch } from 'vue';
  import { ElMessage } from 'element-plus';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import { useViewportAccessGate } from '@/hooks/useViewportAccessGate';
  import { userApis } from '@/axiosModel';
  import { normalizeFileSrc, uploadFileResult } from '@/api/upload';
  import { updateUserSettings } from '@/api/user';
  import { useUserStore } from '@/store';
  import type { UserData } from '@/types/user-type';

  const AVATAR_OPERATE_ID = 1;

  const userStore = useUserStore();
  const { isPC } = useViewportAccessGate();
  const saving = ref(false);
  const uploading = reactive({ avatar: false });

  const profile = reactive({
    uName: userStore.userData.uName,
    phone: userStore.userData.mobilePhone || userStore.userData.phone || '',
    company: userStore.userData.company,
    website: userStore.userData.website,
    about: userStore.userData.about,
    address: userStore.userData.address
  });

  watch(
    () => userStore.userData,
    (userData) => {
      syncProfile(userData);
    },
    { deep: true }
  );

  const handleSave = async () => {
    const uName = profile.uName.trim();
    if (!uName) {
      ElMessage.warning('请输入昵称');
      return;
    }

    saving.value = true;
    try {
      await updateUserSettings({
        uName,
        phone: profile.phone.trim(),
        company: profile.company.trim(),
        website: profile.website.trim(),
        about: profile.about.trim(),
        address: profile.address.trim()
      });

      userStore.userData.uName = uName;
      userStore.userData.mobilePhone = profile.phone.trim();
      userStore.userData.phone = profile.phone.trim();
      userStore.userData.company = profile.company.trim();
      userStore.userData.website = profile.website.trim();
      userStore.userData.about = profile.about.trim();
      userStore.userData.address = profile.address.trim();

      ElMessage.success('保存成功');
    } catch (error) {
      ElMessage.error(getErrorMessage(error, '保存失败，请稍后重试'));
    } finally {
      saving.value = false;
    }
  };

  const triggerUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/jpg';
    input.onchange = async (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) return;

      uploading.avatar = true;
      try {
        const { fileName, fileSrc } = await uploadFileResult(file);
        const uploadedFileName = fileName?.trim();
        if (!uploadedFileName) throw new Error('文件上传失败，未返回文件名');

        await userApis.updateUserLogo({
          fileName: uploadedFileName,
          operateId: AVATAR_OPERATE_ID
        }).req;

        const avatarUrl = normalizeFileSrc(fileSrc);
        if (avatarUrl) userStore.userData.userImg = avatarUrl;
        ElMessage.success('头像更新成功');
      } catch (error) {
        ElMessage.error(getErrorMessage(error, '头像上传失败，请稍后重试'));
      } finally {
        uploading.avatar = false;
      }
    };
    input.click();
  };

  function syncProfile(userData: UserData) {
    profile.uName = userData.uName;
    profile.phone = userData.mobilePhone || userData.phone || '';
    profile.company = userData.company;
    profile.website = userData.website;
    profile.about = userData.about;
    profile.address = userData.address;
  }

  function getErrorMessage(error: unknown, fallback: string) {
    if (isStringRecord(error)) {
      if (typeof error.errorMsg === 'string' && error.errorMsg) return error.errorMsg;
      if (typeof error.msg === 'string' && error.msg) return error.msg;
      if (typeof error.message === 'string' && error.message) return error.message;
    }

    return fallback;
  }

  function isStringRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }
</script>

<style scoped>
  .profile-page {
    max-width: 780px;
    padding: 0;
    box-sizing: border-box;
  }

  .profile-form {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .form-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .avatar-section {
    gap: 8px;
  }

  .field-label {
    color: #333940;
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
  }

  .required {
    margin-left: 2px;
    color: #ff5556;
    font-weight: 400;
  }

  .avatar-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  .avatar-trigger {
    position: relative;
    width: 80px;
    height: 80px;
    padding: 0;
    border: 0;
    border-radius: 2px;
    overflow: hidden;
    background: #f3f4f6;
    cursor: pointer;
    flex-shrink: 0;
  }

  .avatar-trigger:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .avatar-image,
  .avatar-placeholder {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  .avatar-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f3f4f6;
  }

  .avatar-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(2px);
    opacity: 0;
    transition: opacity 0.15s ease;
    pointer-events: none;
  }

  .avatar-trigger:hover .avatar-overlay,
  .avatar-trigger:focus-visible .avatar-overlay {
    opacity: 1;
  }

  .replace-btn {
    min-width: 80px;
    height: 40px;
    padding: 0 12px;
    box-sizing: border-box;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #ffffff;
    color: #333940;
    font-size: 14px;
    line-height: 22px;
    cursor: pointer;
    transition:
      border-color 0.15s,
      color 0.15s;
  }

  .replace-btn:hover:not(:disabled) {
    border-color: #c7d2fe;
    color: #4c4cef;
  }

  .replace-btn:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  .field-hint {
    margin: 0;
    color: #999999;
    font-size: 12px;
    line-height: 16px;
  }

  .profile-input :deep(.el-input__wrapper) {
    width: 100%;
    height: 40px;
    min-height: 40px;
    box-sizing: border-box;
    padding: 10px 12px;
    border-radius: 6px;
    box-shadow: 0 0 0 1px #eeeeee inset;
  }

  .profile-input :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px #0076fe inset;
  }

  .profile-input :deep(.el-input__inner) {
    height: 20px;
    color: #333940;
    font-size: 14px;
    line-height: 20px;
  }

  .save-btn {
    width: 80px;
    height: 40px;
    padding: 0 12px;
    box-sizing: border-box;
    border: 0;
    border-radius: 6px;
    background: #4c4cef;
    color: #ffffff;
    font-size: 14px;
    line-height: 22px;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .save-btn:hover:not(:disabled) {
    background: #3f3fd9;
  }

  .save-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .profile-page--mobile {
    width: 100%;
    max-width: none;
    min-height: 100%;
    padding: 16px 16px 18px;
    background: #ffffff;
  }

  .profile-page--mobile .profile-form {
    gap: 20px;
  }

  .profile-page--mobile .form-section,
  .profile-page--mobile .avatar-section {
    gap: 8px;
  }

  .profile-page--mobile .field-label {
    color: #333940;
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
  }

  .profile-page--mobile .avatar-row {
    gap: 12px;
  }

  .profile-page--mobile .replace-btn {
    width: 96px;
    height: 44px;
    font-weight: 700;
  }

  .profile-page--mobile .profile-input :deep(.el-input__wrapper) {
    height: 44px;
    min-height: 44px;
    padding: 11px 12px;
    border-radius: 6px;
    box-shadow: 0 0 0 1px #eeeeee inset;
  }

  .profile-page--mobile .save-btn {
    width: 100%;
    height: 44px;
    margin-top: 44px;
    border-radius: 6px;
    background: #4c4cef;
    font-weight: 700;
  }
</style>
